import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

import { AccessLevel } from '../model/Identity'
import AuthEndpoints from '../endpoints/AuthEndpoints'

const LOGIN_REDIRECT = import.meta.env.VITE_APP_LOGIN_REDIRECT || ''
const LOGIN_REDIRECT_CLOSE_AFTER =
  import.meta.env.VITE_APP_LOGIN_REDIRECT_CLOSE_AFTER || ''
const LOGOUT_REDIRECT = import.meta.env.VITE_APP_LOGOUT_REDIRECT || ''
const COOKIE_PREFIX = import.meta.env.VITE_APP_COOKIE_PREFIX || ''
const COOKIE_SUFFIX = '__authentigate_token_sanitized'

const COOKIE_NAME = COOKIE_PREFIX + COOKIE_SUFFIX

// The session used to be mirrored into localStorage by zustand's `persist`. It no
// longer is — the auth cookie is the only source of truth — but old blobs linger in
// browsers that ran a previous build, so drop it once on load.
const LEGACY_PERSIST_KEY = 'session-store'

// How often an open tab re-checks the cookie. Cookies fire no storage event, so a
// logout (or a login as someone else) in another tab is only visible by polling.
const COOKIE_POLL_INTERVAL_MS = 15_000

// Set while a deliberate logout navigation is in flight so the cookie watcher does
// not race it with a redirect of its own.
let endingSession = false

const readTokenCookie = (): string => Cookies.get(COOKIE_NAME) || ''

/**
 * Remove the auth cookie in every scope it may have been written in: EMS writes it
 * host-only, while the SSO app writes it on the shared parent domain.
 */
const clearTokenCookie = () => {
  Cookies.remove(COOKIE_NAME)
  Cookies.remove(COOKIE_NAME, { path: '/' })

  const parts = window.location.hostname.split('.')
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = '.' + parts.slice(i).join('.')
    Cookies.remove(COOKIE_NAME, { path: '/', domain })
  }
}

/** Routes that anonymous visitors are allowed to reach without being bounced to the SSO. */
export const isPublicRoute = () => {
  const path = window.location.pathname.toLowerCase()
  return path === '/cne-accreditation' || path === '/ems/cne-accreditation'
}

/** The SSO sign-in URL, or '' when the environment has none configured. */
export const LOGIN_URL = LOGIN_REDIRECT

// Several paths can conclude "no session" at once (initial load, the cookie watcher,
// the redirect screen). Only the first one needs to navigate.
let redirectingToLogin = false

/**
 * Send the user to the SSO app to sign in. EMS has no login form of its own.
 */
export const redirectToLogin = () => {
  if (!LOGIN_REDIRECT || isPublicRoute() || redirectingToLogin) return
  redirectingToLogin = true
  const url = new URL(LOGIN_REDIRECT)
  url.searchParams.set('redirectTo', window.location.href)
  window.location.href = url.toString()
}

interface JWTAccessLevelProvisioning {
  clientId: number
  accessLevel: AccessLevel
}

interface ExternalAccessEntry {
  targetSystem: string
  clientId: number
  operation: string
  parameters?: Record<string, string>
}

interface MasqueradeInfo {
  actualRequesterId: number
  actualRequesterName: string
}

interface Session {
  id: number | string
  firstName: string
  lastName: string
  username: string
  admin: boolean
  accessLevels: Array<JWTAccessLevelProvisioning>
  externalAccess: Array<ExternalAccessEntry>
  exp?: number
  iat?: number
  masquerade?: MasqueradeInfo
}

interface JWTPayload extends Session {
  sub?: string
  iss?: string
  medium?: string
  email?: string
  eventProvisionings?: any[]
  actualRequesterId?: number
  actualRequesterName?: string
}

interface SessionState {
  loggedIn: boolean
  latestToken: string
  session: Session

  // Actions
  logout: () => void
  expired: () => Promise<boolean>
  onLoad: () => Promise<void>
  masqueradeAs: (targetIdentityId: number) => Promise<void>
  endMasquerade: () => Promise<void>
  syncFromCookie: () => void

  // Computed functions
  tokenString: () => string
  hasAdmin: () => boolean
  username: () => string
  hasAccessLevelForAnyone: (accessLevel: AccessLevel) => boolean
  hasAccessLevelFor: (clientId: number, accessLevel: AccessLevel) => boolean
  hasIdentityManagerFor: (clientId: number) => boolean
  hasIdentityManagerForAnyone: () => boolean
  hasScheduleAccessForClient: (clientId: number) => boolean
  hasScheduleAccessForAnyone: () => boolean
  hasScheduleAdminFor: (clientId: number) => boolean
  hasScheduleAdminForAnyone: () => boolean
  canCreateEventsFor: (clientId: number) => boolean
  canCreateEventsForAnyone: () => boolean
  hasPunchClockAccessFor: (clientId: number) => boolean
  hasPunchClockAccessForAnyone: () => boolean
  hasGatekeeperAccessForAnyone: () => boolean
}

// Numeric ranks for the access-level hierarchy. Higher rank = broader permissions.
// Mirrors the backend ordering in PermissionHelper.groovy (IDENTITY_MANAGER → SCHEDULE_MANAGER
// → SCHEDULE_ADMIN → SCHEDULE_AND_PUNCH_CLOCK_ADMIN). Plain string `>=` does NOT work here
// because "SCHEDULE_MANAGER" > "SCHEDULE_ADMIN" alphabetically, the opposite of intended.
const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = {
  [AccessLevel.IDENTITY_MANAGER]: 1,
  [AccessLevel.SCHEDULE_MANAGER]: 2,
  [AccessLevel.SCHEDULE_ADMIN]: 3,
  [AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN]: 4,
}

const rankOf = (level: AccessLevel | string): number =>
  ACCESS_LEVEL_RANK[level as AccessLevel] ?? 0

const defaultSession: Session = {
  id: 0,
  firstName: '',
  lastName: '',
  username: '',
  admin: false,
  accessLevels: [],
  externalAccess: [],
}

const decodeSession = (token: string): Session => {
  const decoded = jwtDecode<JWTPayload>(token)
  const session: Session = {
    id: decoded.id || 0,
    firstName: decoded.firstName || '',
    lastName: decoded.lastName || '',
    username: decoded.username || '',
    admin: decoded.admin || false,
    accessLevels: decoded.accessLevels || [],
    externalAccess: decoded.externalAccess || [],
    exp: decoded.exp,
    iat: decoded.iat,
  }
  if (decoded.actualRequesterId) {
    session.masquerade = {
      actualRequesterId: decoded.actualRequesterId,
      actualRequesterName: decoded.actualRequesterName || 'admin',
    }
  }
  return session
}

const applyToken = (
  token: string,
  set: (partial: Partial<SessionState>) => void,
  writeCookie: boolean = true
) => {
  const session = decodeSession(token)
  set({
    loggedIn: true,
    latestToken: token,
    session,
  })
  if (writeCookie) {
    Cookies.set(COOKIE_NAME, token)
  }
}

const loggedOutState = () => ({
  loggedIn: false,
  latestToken: '',
  session: defaultSession,
})

/**
 * Seed the store straight from the auth cookie at module load. This replaces the old
 * localStorage persistence: the cookie is shared by every tab and cleared by a logout
 * anywhere, so a tab can never come up believing it is still signed in as someone the
 * user has already signed out of.
 */
const initialSessionState = () => {
  try {
    localStorage.removeItem(LEGACY_PERSIST_KEY)
  } catch {
    // Private-mode browsers can throw here; nothing depends on the cleanup.
  }

  const token = readTokenCookie()
  if (!token) return loggedOutState()

  try {
    const session = decodeSession(token)
    const now = Math.floor(Date.now() / 1000)
    if (session.exp && session.exp < now && !import.meta.env.DEV) {
      return loggedOutState()
    }
    return {
      loggedIn: true,
      latestToken: token,
      session,
    }
  } catch {
    return loggedOutState()
  }
}

export const useSessionStore = create<SessionState>()(
  devtools(
      (set, get) => ({
        // Initial state — derived from the cookie, never from localStorage.
        ...initialSessionState(),

        // Actions
        masqueradeAs: async (targetIdentityId: number) => {
          const response = await AuthEndpoints.masquerade(targetIdentityId)
          if (response.token) {
            applyToken(response.token, set)
            // Drop any cached page-level state by reloading. The new session is
            // the target's session — everything in memory was scoped to the admin.
            window.location.href = '/'
          }
        },

        endMasquerade: async () => {
          const response = await AuthEndpoints.endMasquerade()
          if (response.token) {
            applyToken(response.token, set)
            window.location.href = '/'
          }
        },

        logout: () => {
          endingSession = true
          clearTokenCookie()
          set(loggedOutState())

          // The SSO app owns the session; its logout page clears the shared cookie
          // and any server-side session. Falling back to the login page would sign
          // the user straight back in, so only fall back when nothing is configured.
          if (LOGOUT_REDIRECT) {
            window.location.href = LOGOUT_REDIRECT
          } else {
            console.warn(
              'VITE_APP_LOGOUT_REDIRECT is not set; falling back to the login redirect'
            )
            redirectToLogin()
          }
        },

        expired: async () => {
          try {
            const isAlive = await AuthEndpoints.ping()
            if (isAlive) return false
          } catch {
            // Treat a failed ping as an expired session.
          }

          // An expired session goes back to login (which preserves where the user
          // was), not to the SSO logout page.
          endingSession = true
          clearTokenCookie()
          set(loggedOutState())
          redirectToLogin()
          return true
        },

        /**
         * Re-read the auth cookie and reconcile this tab with it. Called on an
         * interval and whenever the tab regains focus, so a logout — or a login as a
         * different user — in another tab takes effect here instead of leaving this
         * tab showing the previous user.
         */
        syncFromCookie: () => {
          if (endingSession) return

          const token = readTokenCookie()
          const { latestToken, loggedIn, session } = get()

          if (!token) {
            if (loggedIn) {
              endingSession = true
              set(loggedOutState())
              redirectToLogin()
            }
            return
          }

          if (token === latestToken) return

          try {
            const next = decodeSession(token)
            if (loggedIn && String(next.id) !== String(session.id)) {
              // Someone else signed in on this browser. Everything held in memory is
              // scoped to the previous user, so start over rather than swap identities
              // underneath the mounted tree.
              window.location.reload()
              return
            }
            // Same user with a refreshed token: adopt it without rewriting the cookie.
            applyToken(token, set, false)
          } catch {
            endingSession = true
            clearTokenCookie()
            set(loggedOutState())
            redirectToLogin()
          }
        },

        onLoad: async () => {
          if (isPublicRoute()) return

          // In development, seed the auth cookie from the env var if no cookie exists yet
          if (import.meta.env.DEV && import.meta.env.VITE_APP_SET_DEV_TOKEN) {
            if (!readTokenCookie()) {
              Cookies.set(COOKIE_NAME, import.meta.env.VITE_APP_SET_DEV_TOKEN)
            }
          }

          const token = readTokenCookie()

          if (!token) {
            set(loggedOutState())
            redirectToLogin()
            return
          }

          try {
            const decoded = jwtDecode<JWTPayload>(token)

            const now = Math.floor(Date.now() / 1000)
            const isDev = import.meta.env.DEV
            if (decoded.exp && decoded.exp < now && !isDev) {
              clearTokenCookie()
              set(loggedOutState())
              redirectToLogin()
              return
            }

            applyToken(token, set, false)
          } catch (error) {
            console.error('Invalid token on load:', error)
            clearTokenCookie()
            set(loggedOutState())
            redirectToLogin()
          }
        },

        // Computed functions
        tokenString: () => {
          return get().latestToken
        },

        hasAdmin: () => {
          return get().session.admin
        },

        username: () => {
          return get().session.username
        },

        hasAccessLevelForAnyone: (accessLevel: AccessLevel) => {
          const { session } = get()
          if (session.admin) return true
          const needed = rankOf(accessLevel)
          return session.accessLevels.some(
            (al) => rankOf(al.accessLevel) >= needed
          )
        },

        hasAccessLevelFor: (clientId: number, accessLevel: AccessLevel) => {
          const { session } = get()
          if (session.admin) return true
          const needed = rankOf(accessLevel)
          return session.accessLevels.some(
            (al) => al.clientId === clientId && rankOf(al.accessLevel) >= needed
          )
        },

        // IDENTITY_MANAGER+ for this client — gates identity creation / provisioning.
        hasIdentityManagerFor: (clientId: number) => {
          return get().hasAccessLevelFor(clientId, AccessLevel.IDENTITY_MANAGER)
        },

        hasIdentityManagerForAnyone: () => {
          return get().hasAccessLevelForAnyone(AccessLevel.IDENTITY_MANAGER)
        },

        // SCHEDULE_MANAGER+ for this client — gates spot assignment / schedule edits.
        hasScheduleAccessForClient: (clientId: number) => {
          return get().hasAccessLevelFor(clientId, AccessLevel.SCHEDULE_MANAGER)
        },

        hasScheduleAccessForAnyone: () => {
          return get().hasAccessLevelForAnyone(AccessLevel.SCHEDULE_MANAGER)
        },

        // SCHEDULE_ADMIN+ for this client — gates event edit, shift create/edit/delete, role/location/position/accreditation edits.
        hasScheduleAdminFor: (clientId: number) => {
          return get().hasAccessLevelFor(clientId, AccessLevel.SCHEDULE_ADMIN)
        },

        hasScheduleAdminForAnyone: () => {
          return get().hasAccessLevelForAnyone(AccessLevel.SCHEDULE_ADMIN)
        },

        // Whether the user can spawn new events for this client. Backend additionally requires the
        // user is not restricted to a specific event-filter set; that field is not exposed in the
        // JWT, so the frontend only checks the level. A restricted user clicking the button still
        // gets a 400 from the backend.
        canCreateEventsFor: (clientId: number) => {
          return get().hasScheduleAdminFor(clientId)
        },

        canCreateEventsForAnyone: () => {
          return get().hasScheduleAdminForAnyone()
        },

        // SCHEDULE_AND_PUNCH_CLOCK_ADMIN — gates manual punch in/out, break editing.
        hasPunchClockAccessFor: (clientId: number) => {
          return get().hasAccessLevelFor(
            clientId,
            AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
          )
        },

        hasPunchClockAccessForAnyone: () => {
          return get().hasAccessLevelForAnyone(
            AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
          )
        },

        // External access: `targetSystem=onsite, operation=GATEKEEPER` grants visit-log access.
        hasGatekeeperAccessForAnyone: () => {
          const { session } = get()
          if (session.admin) return true
          return session.externalAccess.some(
            (e) => e.targetSystem === 'onsite' && e.operation === 'GATEKEEPER'
          )
        },
      }),
    {
      name: 'session-store',
    }
  )
)

/**
 * Watch the auth cookie for the lifetime of the app. Cookies emit no events, so this
 * polls and also checks whenever the tab is refocused — the moment a user is most
 * likely to have just signed out or signed in as someone else in another tab.
 *
 * Returns a cleanup function.
 */
export const startSessionWatch = (): (() => void) => {
  const check = () => useSessionStore.getState().syncFromCookie()

  const onVisibility = () => {
    if (document.visibilityState === 'visible') check()
  }

  const timer = window.setInterval(check, COOKIE_POLL_INTERVAL_MS)
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', onVisibility)

  return () => {
    window.clearInterval(timer)
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
